import * as R from 'ramda';
import { EntitlementType } from '../../../entitlementTypes/entitlementType';
import { isNilOrEmpty } from '../../../language/module';
import { isOlderThan } from '../../../language/time';
import { GuestDetails } from '../../guestDetails';
import { isIkonPassForSeason } from '../module';
import { Waiver } from './waiver';
import { WaiverSignature } from './waiverSignature';
import { WAIVER_STATUS } from './waiverStatus';
import ImmutableMap from '../../../language/ImmutableMap';
import { getResource } from '../../../language/resources/getResource';


/**
 * We can't fetch waiver titles from the API yet, so these are our hard-coded, made-up titles for waivers.
 */
export const WAIVER_TITLE = {
  IKON_WAIVER_TITLE_18_19: '18-19 Ikon Liability Waiver',
  IKON_WAIVER_TITLE_19_20: '19-20 Ikon Liability Waiver',
  IKON_WAIVER_TITLE_20_21: '20-21 Ikon Liability Waiver',

  // Employee
  ALTERRA_MOUNTAIN_EMPLOYEE_WAIVER_TITLE:           'Alterra Mountain Employee Liability Waiver',
  ALTERRA_MOUNTAIN_EMPLOYEE_DEPENDENT_WAIVER_TITLE: 'Alterra Mountain Employee Dependent Liability Waiver',
};


/**
 * We can't fetch waiver versions from the API yet, so these are our hard-coded (not made-up) waiver versions.
 */
export const WAIVER_VERSION = {
  IKON_WAIVER_VERSION_18_19: '998',
  IKON_WAIVER_VERSION_19_20: '981',
  IKON_WAIVER_VERSION_20_21: '984',

  // Employee
  ALTERRA_MOUNTAIN_EMPLOYEE_WAIVER_VERSION:           '982',
  ALTERRA_MOUNTAIN_EMPLOYEE_DEPENDENT_WAIVER_VERSION: '983',

};


/**
 * The version codes for the waivers that we care about the user signing.
 * Currently no support for multiple waivers.
 * Currently no support for mapping between entitlement and waiver.
 * Order: chronological
 */
export const IKON_PASS_WAIVER_VERSIONS = [
  WAIVER_VERSION.IKON_WAIVER_VERSION_18_19,
  WAIVER_VERSION.IKON_WAIVER_VERSION_19_20,
  WAIVER_VERSION.IKON_WAIVER_VERSION_20_21,
  WAIVER_VERSION.ALTERRA_MOUNTAIN_EMPLOYEE_WAIVER_VERSION,
  WAIVER_VERSION.ALTERRA_MOUNTAIN_EMPLOYEE_DEPENDENT_WAIVER_VERSION,
];

export const SIGNABLE_WAIVERS = [
  WAIVER_VERSION.IKON_WAIVER_VERSION_20_21,
  WAIVER_VERSION.ALTERRA_MOUNTAIN_EMPLOYEE_WAIVER_VERSION,
  WAIVER_VERSION.ALTERRA_MOUNTAIN_EMPLOYEE_DEPENDENT_WAIVER_VERSION,
];

/**
 * Given a waiver version, returns a UI-friendly title for that waiver
 */
export const waiverTitleFor = (
    resources: Record<string, any>,
    version: string | number,
    waiverLookup: ImmutableMap<string>,
    isLoading: boolean,
    error: boolean
  ): string => {
  if (isLoading) {
    return getResource(resources, 'WAIVER_MODAL_LOADING');
  }

  if (error) {
    return getResource(resources, 'WAIVER_ERROR');
  }

  return waiverLookup.get(`${version}`) || getResource(resources, 'WAIVER_UNKNOWN');
};


/**
 * Returns whether or not the given waiver's version equals the given version. Curried for clean filtering/mapping/etc
 */
export const hasVersion = (version: string | number) => (waiver: Waiver): boolean => waiver.version === `${version}`;


/**
 * Runs validation to determine based on iso date string if a being is over eighteen years old
 */
export const isOverEighteenYearsOld = (
  dateOfBirth: string | Date,
) => isNilOrEmpty(dateOfBirth) ? false : isOlderThan(18, dateOfBirth);


/**
 * Returns a user-displayable string error if the waiver signer info is in an invalid state. Things like missing first
 * name, and the signer's date of birth are taken into account. Returns undefined if everything checks out.
 */
export const findWaiverSigningError = (resources: Record<string, any>, signature: WaiverSignature): string | undefined => {
  if (isNilOrEmpty(signature.firstName)
      || isNilOrEmpty(signature.lastName)
      || isNilOrEmpty(signature.dateOfBirth)
  ) {
    return getResource(resources, 'WAIVER_ALL_FIELDS_REQUIRED');
  }

  if (!isOverEighteenYearsOld(signature.dateOfBirth)) {
    return getResource(resources, 'WAIVER_ERROR_OVER_18');
  }

  return undefined;
};


/**
 * Returns whether or not the given waiver object is a signed waiver object.
 */
export const isSigned = (waiver: Waiver): boolean => waiver.status === WAIVER_STATUS.SIGNED;


/**
 * Whether or not the given waiver? is expired.
 * Includes optional reference date to compare against (defaults to today)
 * If the waiver is undefined or doesn't have an expirationDate expect back `false`
 */
export const isExpired = (
  waiver: Waiver | undefined,
  refDate?: Date | string,
) => waiver?.expirationDate && new Date(waiver.expirationDate) < (refDate ? new Date(refDate) : new Date());


/**
 * Returns an array of all waiver types which are required to enable use of entitlements of the types provided
 * (or undefined if no entitlement types are passed in)
 * @param entitlementTypes The entitlement types whose required waiver types we wish to retrieve
 */
export const getRequiredWaivers = (
  entitlementTypes: EntitlementType[] | undefined | (EntitlementType | undefined)[] | undefined,
): number[] | undefined => {
  if (isNilOrEmpty(entitlementTypes)) {
    return undefined;
  }

  const flattenedEntitlementTypes = Array.isArray(entitlementTypes) ? entitlementTypes.flat() : [entitlementTypes];
  const waiverData = R.flatten(flattenedEntitlementTypes.map(_type => _type?.requiredWaiverVersions || []));
  return R.uniq(waiverData.map(waiver => waiver?.version)).filter(Boolean);
};


/**
 * Predicate for whether or not the given guest (or undefined) has a valid, signed a waiver.
 * Valid = not-expired && with a recognized version code.
 * Includes hard-coded logic for specific passes
 */
export const shouldSignWaiver = (
  guest: GuestDetails | undefined,
  requiredWaiverVersions: number[] | undefined,
): boolean => {
  if (isNilOrEmpty(guest?.waivers) || !requiredWaiverVersions?.length) {
    return false;
  }

  const has2021Pass         = R.any(isIkonPassForSeason('2020-2021'), guest?.entitlements || []);
  const hasSigned2021Waiver = R.any(
    waiverVersion => R.any(
      waiver => waiver.version === WAIVER_VERSION.IKON_WAIVER_VERSION_20_21 && isSigned(waiver),
      guest!.waivers,
    ),
    requiredWaiverVersions.map(R.toString),
  );

  if (has2021Pass && !hasSigned2021Waiver) {
    return false;
  }

  // TODO: This should be `.all`, but ATM waiver support is manual and no entitlements require multiple waivers, but the
  //       API treats `requiredWaiverVersions` as a "any of these" instead of "all of these"
  return R.any(
    waiverVersion => R.any(
      waiver => waiver.version === waiverVersion && isSigned(waiver),
      guest!.waivers,
    ),
    requiredWaiverVersions.map(R.toString),
  );
};


/**
 * Predicate for whether or not the given guest (or undefined) has a valid, signed a waiver.
 * Valid = not-expired && with a recognized version code
 */
export const hasValidWaiver = (
  guest: GuestDetails | undefined,
  requiredWaiverVersions: number[] | undefined,
): boolean => {
  if (isNilOrEmpty(guest?.waivers) || !requiredWaiverVersions?.length) {
    return false;
  }

  return R.any(
    waiverVersion => R.any(
      waiver => waiver.version === waiverVersion && isSigned(waiver) && !isExpired(waiver),
      guest!.waivers,
    ),
    requiredWaiverVersions.map(R.toString),
  );
};

export const hasSignedWaiver = (
  version: string,
  waivers: Waiver[],
): boolean => R.any(waiver => waiver.version === version && isSigned(waiver), waivers);

export const isWaived = (
  entitlement: EntitlementType | undefined,
  waivers: Waiver[],
): boolean => entitlement
  ? R.any(version =>
    hasSignedWaiver(`${version}`, waivers), entitlement.requiredWaiverVersions.map(it => it.version))
  : false;


export const isSignatureEnabled = (
  entitlementTypes: EntitlementType[] | undefined,
   waiverVersion: number
): boolean => getEnabledSignatures(entitlementTypes).includes(waiverVersion);


export const getEnabledSignatures = (entitlementTypes: EntitlementType[] | undefined): number[] => {
  if(isNilOrEmpty(entitlementTypes)){
    return [];
  }

  const waiverData = R.flatten(entitlementTypes!.map(it => it.requiredWaiverVersions));

  return R.uniq(waiverData.filter(it => it.enableSignature).map(it => it.version));
};
